import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import NavigationSearch from "./NavigationSearch";
import CtaLink from "../Global/CtaLink";
import Flyout from "./Flyout";
import Image from "../Global/Image/Image";
import { useOverrideDimensions } from "./hooks";
import "./MobileNavigation.scss";
import { GetChannelRoot } from "../../helpers/LinkHelper";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import useToggle from "../../hooks/useToggle";
import { isValidLink } from "./navigationUtils";

const NAV_ONE = "nav-1";
const NAV_TWO = "nav-2";
const NAV_THREE = "nav-3";
const NAV_FOUR = "nav-4";

function MobileNavigation({ navDocument }) {
  const [menuOpen, toggleMenu, setMenuOpen] = useToggle(false);
  const [searchOpen, toggleSearch, setSearchOpen] = useToggle(false);
  const [activeNav, setActiveNav] = useState("");
  const isFirstRender = useRef(true);

  const sendDataToGTM = useGTMDispatch();

  useEffect(() => {
    //if menu is open in mobile we turn off scrolling
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    if (isFirstRender.current) {
      isFirstRender.current = false; // toggle flag after first render/mounting
      return;
    }
  });

  const {
    logoAltText = null,
    logo = null,
    mainMenuLinkLabelOne = null,
    mainMenuLinkLabelTwo = null,
    mainMenuLinkLabelThree = null,
    mainMenuLinkLabelFour = null,
    regionSelectLink = null,
    howToBuyLink = null,
    viewMyListLink = null,
    mainMenuLinkFlyoutOne = null,
    mainMenuLinkFlyoutTwo = null,
    mainMenuLinkFlyoutThree = null,
    mainMenuLinkFlyoutFour = null,
    searchButtonLabel = null,
  } = navDocument.getData();

  const { logoWidth = 0, logoHeight = 0 } = useOverrideDimensions();
  const mobileHeaderLogoWidth = "125";
  const mobileHeaderLogoHeight = "45";

  // Memoized navigation items configuration
  const navItems = useMemo(() => [
    { id: NAV_ONE, label: mainMenuLinkLabelOne, flyout: mainMenuLinkFlyoutOne },
    { id: NAV_TWO, label: mainMenuLinkLabelTwo, flyout: mainMenuLinkFlyoutTwo },
    { id: NAV_THREE, label: mainMenuLinkLabelThree, flyout: mainMenuLinkFlyoutThree },
    { id: NAV_FOUR, label: mainMenuLinkLabelFour, flyout: mainMenuLinkFlyoutFour },
  ].filter(item => item.label), [mainMenuLinkLabelOne, mainMenuLinkLabelTwo, mainMenuLinkLabelThree, mainMenuLinkLabelFour, mainMenuLinkFlyoutOne, mainMenuLinkFlyoutTwo, mainMenuLinkFlyoutThree, mainMenuLinkFlyoutFour]);

  // Memoized validation for howToBuy link
  const howToBuyCheck = useMemo(() => {
    return isValidLink(howToBuyLink);
  }, [howToBuyLink]);

  // Memoized utility items configuration
  const utilityItems = useMemo(() => [
    { link: regionSelectLink, icon: "mobile-navigation__utility-list-item-link-icon darticon-region" },
    { link: howToBuyCheck ? howToBuyLink : null, icon: "mobile-navigation__utility-list-item-link-icon darticon-webpages" },
    { link: viewMyListLink, icon: "mobile-navigation__utility-list-item-link-icon darticon-viewlist" },
  ].filter(item => item.link), [regionSelectLink, howToBuyCheck, howToBuyLink, viewMyListLink]);

  // Handles Link Click
  // If internal link, close hamburger menu and reset to primary nav list
  const handleLinkClick = useCallback((item) => {
    if (item.internalLink) {
      setMenuOpen(false);
      setActiveNav("");
    }
  }, [setMenuOpen]);

  //set active nav item, or if same nav item unset
  const _toggleNavItems = useCallback((navItem) => {
    setActiveNav(prev => prev === navItem ? "" : navItem);
  }, []);

  // Toggle Hamburger Menu
  const toggleHamburgerMenu = useCallback(() => {
    toggleMenu();
    // If menuOpen = false, reset to primary nav list
    if (menuOpen) {
      setActiveNav("");
    }
  }, [toggleMenu, menuOpen]);

  const sendMobileHeaderGtm = useCallback((level) => {
    sendDataToGTM({
      event: "navigationClick",
      navigationType: "header",
      navigationLevel: level,
    });
  }, [sendDataToGTM]);

  return (
    <div className={`mobile-navigation`}>
      <div className="mobile-navigation__top-bar">
        <div className="mobile-navigation__top-bar-wrapper">
          <button
            className={`mobile-navigation__hamburger ${
              !menuOpen ? "darticon-menu" : "darticon-close"
            } `}
            id="goTop"
            onClick={toggleHamburgerMenu}
            aria-label={menuOpen ? "Close Menu" : "Open Menu"}
          ></button>
          <Link
            to={GetChannelRoot}
            onClick={() => {
              setMenuOpen(false);
              setActiveNav("");
            }}
          >
            <Image
              className="mobile-navigation__logo"
              src={logo}
              alt={logoAltText}
              width={Number(logoWidth) === 0 ? mobileHeaderLogoWidth : logoWidth}
              height={Number(logoHeight) === 0 ? mobileHeaderLogoHeight : logoHeight}
            />
          </Link>
          <button
            className="mobile-navigation__search darticon-headersearch"
            onClick={() => {
              sendMobileHeaderGtm(searchButtonLabel);
              toggleSearch();
            }}
            aria-label={searchOpen ? "Close Searchbar" : "Open Searchbar"}
          ></button>
        </div>
      </div>
      {menuOpen && (
        <nav className="mobile-navigation__nav">
          <div className="mobile-navigation__nav-wrapper">
            <ul className="mobile-navigation__nav-list">
              {navItems.map(({ id, label }) => (
                <li key={id} className="mobile-navigation__nav-list-item">
                  <button
                    className="mobile-navigation__nav-list-item-link"
                    onClick={() => {
                      sendMobileHeaderGtm(label);
                      _toggleNavItems(id);
                    }}
                  >
                    {label}
                    <span className="mobile-navigation__nav-list-item-link-icon darticon-arrow"></span>
                  </button>
                </li>
              ))}
            </ul>
            <ul className="mobile-navigation__utility-list">
              {utilityItems.map((item, index) => (
                <li key={index} className="mobile-navigation__utility-list-item">
                  <CtaLink
                    link={item.link}
                    icon={item.icon}
                    className={`mobile-navigation__utility-list-item-link`}
                    onClick={() => handleLinkClick(item.link)}
                  />
                </li>
              ))}
            </ul>
            {activeNav && (
              <div className="mobile-navigation__flyout-wrapper">
                {navItems.map(({ id, label, flyout }) => {
                  if (activeNav === id && flyout) {
                    return (
                      <Flyout
                        key={id}
                        flyoutHeaderLabel={label}
                        flyoutRef={flyout}
                        closeActiveSubNav={setActiveNav}
                        setMenuOpen={setMenuOpen}
                      />
                    );
                  }
                  return null;
                })}
              </div>
            )}
          </div>
        </nav>
      )}
      {searchOpen && (
        <div className="mobile-navigation__search-wrapper">
          <NavigationSearch
            setSearchOpen={setSearchOpen}
            navDocument={navDocument}
          ></NavigationSearch>
        </div>
      )}
    </div>
  );
}

export default MobileNavigation;
